import type { BuyerWalletPayment, SavedCard } from "./wallet-api";

/**
 * The card-entry island's contract: the one way anything outside it — the
 * checkout core, the web renderer, a native app — reaches the card.
 *
 * The island is the only place card details and the processor's card code
 * live: Stripe's split fields, the in-field saved-card chip or "Save with
 * Lissen" tick, confirming the card on the platform SetupIntent, and
 * confirming the payment the engine prepared on the organizer's account —
 * the bank's checks on both included. Whoever hosts it hands in the setup
 * and the buyer's address, hears what the fields say, on submit gets back
 * an opaque method id, and on confirm the payment's id — never a processor
 * object. Nothing here names Stripe.
 *
 * Two adapters speak it, both in `apps/ticketing-checkout/src/island/`: in
 * the checkout document the island is a component (card-island.tsx) and a
 * function (confirm-payment.ts) whose props, callbacks and answers are these
 * shapes; on its own page (`/checkout/card`, card-page.tsx, which a native
 * app shows in a WebView) the same shapes travel as messages
 * (card-island-wire.ts).
 */

/** What the island needs to confirm a card, handed in by the host: the
 *  processor's public key and the SetupIntent the engine made for it. */
export type CardSetupRef = { publishableKey: string; clientSecret: string };

export type CardDetails = { brand: string; last4: string };

/** A card entered and set up, or the buyer's sentence for why not. */
export type CardResult =
  | {
      paymentMethodId: string;
      /** Brand and last four, when the processor says. */
      card: CardDetails | null;
      /** The bank checked the card as it went in: its check on the payment
       *  that follows is then the bank asking a second time. */
      challenged: boolean;
    }
  | { error: string };

/** Shows the bank's own page at `url` and settles once the buyer has
 *  answered it; what the bank decided the island reads from the processor. */
export type BankPage = (url: string) => Promise<void>;

/** The island once it can take a submit. `bank` is how the host draws the
 *  bank's page; an island on its own page draws it itself. */
export type CardIsland = {
  submit: (request: { email: string; bank: BankPage }) => Promise<CardResult>;
};

/** What the fields say as the buyer types. `brand` is the processor's
 *  guess from the number, for naming a card the processor later does not. */
export type CardChange = {
  complete: boolean;
  empty: boolean;
  brand: string | null;
};

/** The in-field choice the island draws: the newest saved card as a chip,
 *  or, once a number is typed (or there is none), the Save tick. */
export type CardChoice = { selected: string | null; remember: boolean };

/** What the host says about the fields beyond the setup. */
export type CardIslandState = CardChoice & {
  /** The buyer's saved cards, newest first. */
  savedCards: Array<SavedCard>;
  /** The saved cards are still being fetched: the chip shimmers. */
  locked: boolean;
  /** A payment is under way: the fields take nothing. */
  disabled: boolean;
};

// ---- confirming the payment ----------------------------------------------------

/** Why the bank checks a payment: the line over its page says which.
 *  `payment_again` follows a card the bank checked a moment ago as it was
 *  set up, so the buyer is told why it asks a second time. */
export type PaymentReason = "payment" | "payment_again";

/** A payment the engine prepared on the organizer's account, and why the
 *  bank would be checking it. The method is any one the engine cloned
 *  there: a saved card, a card typed now, or a wallet sheet's (Apple or
 *  Google Pay, made on the platform by whoever showed the sheet). */
export type PaymentToConfirm = {
  payment: BuyerWalletPayment;
  reason: PaymentReason;
};

/** The states a confirmed payment may be left in: paid, or paid pending
 *  the processor or capture. Anything else is a refusal. */
export type PaymentStatus = "succeeded" | "processing" | "requires_capture";

/** A payment confirmed, or the buyer's sentence for why not. */
export type PaymentResult =
  | { paymentIntentId: string; status: PaymentStatus }
  | { error: string };

/**
 * The island confirming a payment. `bank` is how the host draws the bank's
 * page (under `reason`); an island on its own page draws it itself and
 * ignores it. A refusal answers `{ error }`; a failure to reach the
 * processor at all may throw.
 *
 * A payment the engine has confirmed already says so
 * (`payment_intent_status`): one that wants the bank's check is read, not
 * confirmed again, and its check drawn as after the island's own; one that
 * went through is answered as it stands.
 */
export type ConfirmPayment = (
  request: PaymentToConfirm & { bank: BankPage },
) => Promise<PaymentResult>;

/** The organizer's account a payment is made on, as the engine named it
 *  when it prepared one: what the processor's client needs to act there. */
export type PaymentAccount = Pick<
  BuyerWalletPayment,
  "publishable_key" | "connected_account_id"
>;

/**
 * A snapshot of this device for the processor's fraud screening, made on
 * the account the payment will be on, for the engine to confirm a payment
 * with (Stripe's Radar session). `null` when none can be made, quickly: it
 * is never why a buyer cannot pay, only why the payment is confirmed here
 * rather than by the engine. It is asked for at the press, and goes with
 * that payment alone: the next one asks again, and gets another.
 */
export type DeviceSession = (account: PaymentAccount) => Promise<string | null>;

/**
 * Begin making the device session a payment on `account` will ask for, so
 * that the press finds it made rather than waiting for it. Nothing is
 * answered; asking again while one is on hand begins nothing.
 */
export type PrepareDeviceSession = (account: PaymentAccount) => void;
